/**
 * RemoveAccess Controller - Handles ONLY RemoveAccess flows
 */

import type { Dispatch } from '@reduxjs/toolkit';

import type { RootState } from '../../../services/serviceTypes';
import {
  addCompletedStep,
  addMessage,
  addMessageToEntitlement,
  removeMessage,
  removeMessagesBySubType,
  removeSkippedStep,
  setRemoveAccessEntitlementIndex,
  setRemoveAccessPrompt,
  updateStepFormData,
} from '../data-store/copilotSlice';

import {
  buildRemoveAccessCumulativePayload,
  buildRemoveAccessPayloadFromRedux,
} from './mappingReplacementUtils';
import { isEditMode } from './modeUtils';
import {
  isEditModeFromUrl,
  processPayloadByMode,
} from './payloadProcessingUtils';

interface RemoveAccessControllerConfig {
  dispatch: Dispatch;
  getState: () => unknown;
  services: {
    saveUseCaseConfiguration: (payload: unknown) => Promise<unknown>;
  };
}

export default class RemoveAccessController {
  private dispatch: Dispatch;

  private getState: () => unknown;

  private services: RemoveAccessControllerConfig['services'];

  private removeAccessAttributePayload: Record<string, unknown> | null = null;

  showFinalConfirmation?: () => void;

  constructor(config: RemoveAccessControllerConfig) {
    this.dispatch = config.dispatch;
    this.getState = config.getState;
    this.services = config.services;
  }

  private cleanupLoaderMessages(): void {
    const currentState = this.getState() as {
      copilot?: { messages?: { id?: string; subType?: string }[] };
    };

    const attributeMappingMessages =
      currentState.copilot?.messages?.filter(
        (msg) => msg.subType === 'attributeMappingNext' && !!msg.id
      ) ?? [];

    attributeMappingMessages.forEach((msg) => {
      if (msg.id) {
        this.dispatch(removeMessage(msg.id));
      }
    });
  }

  private messagesContainEntitlementForm(entitlementIndex: number): boolean {
    const state = this.getState() as {
      copilot?: {
        messages?: {
          type?: string;
          stepName?: string;
          formData?: { entitlementIndex?: number };
        }[];
      };
    };

    return (
      state.copilot?.messages?.some(
        (msg) =>
          msg.type === 'step_form' &&
          msg.stepName === 'RemoveAccess' &&
          msg.formData?.entitlementIndex === entitlementIndex
      ) ?? false
    );
  }

  private async processRemoveAccessPrompt(
    userPrompt: string,
    currentIndex: number
  ): Promise<void> {
    const state = this.getState() as RootState;

    // Store prompt in Redux
    this.dispatch(
      setRemoveAccessPrompt({ index: currentIndex, prompt: userPrompt })
    );

    // Get payload and build updated prompts
    const removeAccessConfirmation =
      state.copilot.stepFormData.RemoveAccessConfirmation;
    let removeAccessPayload: Record<string, unknown>;

    if (
      removeAccessConfirmation &&
      typeof removeAccessConfirmation === 'object' &&
      'payload' in removeAccessConfirmation &&
      removeAccessConfirmation.payload &&
      typeof removeAccessConfirmation.payload === 'object'
    ) {
      removeAccessPayload = removeAccessConfirmation.payload as Record<
        string,
        unknown
      >;
    } else {
      removeAccessPayload = removeAccessConfirmation ?? {};
    }

    const allPrompts = {
      ...state.copilot.removeAccessPrompts,
      [currentIndex]: userPrompt,
    };
    const { entitlements } = state.copilot.entitlementIteration;
    const isInEditMode = isEditModeFromUrl();

    let payloadToProcess = removeAccessPayload;

    // In edit mode, rebuild from original template
    if (isInEditMode) {
      const removeAccessTemplate = state.copilot.parsedTemplates
        .RemoveAccess as { attributeDefaultValue?: string } | undefined;
      const originalTemplate = removeAccessTemplate?.attributeDefaultValue;

      if (originalTemplate && typeof originalTemplate === 'string') {
        payloadToProcess = buildRemoveAccessPayloadFromRedux(
          originalTemplate,
          entitlements,
          state.copilot.stepFormData ?? {},
          allPrompts
        );
      }
    }

    // Process and save payload
    const processedPayload = processPayloadByMode(isInEditMode, {
      payload: payloadToProcess,
      currentIndex,
      allPrompts,
      currentPrompt: userPrompt,
      totalEntitlements: entitlements.length,
    });

    let { endpointKey } = state.copilot;

    if (!endpointKey) {
      const urlParams = new URLSearchParams(window.location.search);

      endpointKey = parseInt(urlParams.get('endpointKey') ?? '0', 10) || null;
    }

    await this.services.saveUseCaseConfiguration({
      applicationtype: 'CUA',
      endpointKey,
      body: processedPayload,
    });

    // Handle progression
    const totalEntitlements = entitlements;
    const isLastTemplate = currentIndex >= totalEntitlements.length - 1;

    if (isLastTemplate) {
      this.dispatch(addCompletedStep('RemoveAccess'));
      this.dispatch(removeSkippedStep('RemoveAccess'));

      if (!isEditMode(this.getState)) {
        this.showFinalConfirmation?.();
      } else {
        this.dispatch(
          addMessageToEntitlement({
            entitlementIndex: currentIndex,
            entitlementType: 'removeAccess',
            message: {
              id: Date.now().toString(),
              type: 'ai_response',
              subType: 'removeAccessUpdateSuccess',
              text: 'You have updated the Remove Access Entitlements. I will add to the Configuration.',
              timestamp: Date.now(),
              addAccessEntitlementIndex: -1,
              removeAccessEntitlementIndex: -1,
            },
          })
        );
      }
    } else {
      this.showNextEntitlementForm(currentIndex + 1);
    }
  }

  public showNextEntitlementForm(nextIndex: number): void {
    const state = this.getState() as RootState;
    const totalEntitlements = state.copilot.entitlementIteration.entitlements;

    // Check if editing existing sequence
    const isEditingExistingSequence =
      isEditModeFromUrl() &&
      nextIndex < totalEntitlements.length &&
      this.messagesContainEntitlementForm(nextIndex);

    if (
      isEditingExistingSequence ||
      this.messagesContainEntitlementForm(nextIndex)
    ) {
      return;
    }

    const nextEntitlementValue =
      totalEntitlements[nextIndex]?.value ?? 'Unknown';

    this.dispatch(
      addMessage({
        id: Date.now().toString(),
        type: 'ai_response',
        subType: 'attributeMappingNext',
        values: [nextEntitlementValue],
        loader: true,
        timestamp: Date.now(),
      })
    );

    if (this.removeAccessAttributePayload) {
      this.removeAccessAttributePayload = {
        ...this.removeAccessAttributePayload,
        entitlementIndex: nextIndex,
      };
    }

    this.dispatch(setRemoveAccessEntitlementIndex(nextIndex));

    setTimeout(() => {
      this.cleanupLoaderMessages();
      const currentState = this.getState() as {
        copilot?: { parsedTemplates?: { RemoveAccess?: unknown } };
      };
      const parsedTemplates = currentState.copilot?.parsedTemplates;

      this.dispatch(
        addMessageToEntitlement({
          entitlementIndex: nextIndex,
          entitlementType: 'removeAccess',
          message: {
            id: `remove-access-mapping-${nextIndex}-${Date.now()}`,
            type: 'step_form',
            subType: 'removeAccess',
            timestamp: Date.now(),
            heading: 'Remove Access Attribute Mapping',
            fields:
              parsedTemplates?.RemoveAccess &&
              typeof parsedTemplates.RemoveAccess === 'object'
                ? [parsedTemplates.RemoveAccess]
                : [],
            formData: { entitlementIndex: nextIndex },
            isCollapseable: false,
            buttonLabel: 'Save',
            stepName: 'RemoveAccess',
          },
        })
      );
    }, 2000);
  }

  // Public methods
  handleRemoveAccessAttributeMappingSubmit(
    formData: Record<string, unknown>,
    isValid: boolean
  ): void {
    if (isValid) {
      this.showRemoveAccessConfirmation(formData);
    }
  }

  public handleUserPrompt(userPrompt: string): void {
    this.handleRemoveAccessPromptWithIndex(userPrompt).catch(() => {});
  }

  public async handleRemoveAccessPromptWithIndex(
    userPrompt: string
  ): Promise<void> {
    this.dispatch(removeMessagesBySubType('movingToNextStep'));

    const state = this.getState() as RootState;
    // Get the index from state. It should be 0. Fallback to 0 just in case.
    const currentIndex = state.copilot.removeAccessEntitlementIndex ?? 0;

    await this.processRemoveAccessPrompt(userPrompt, currentIndex);
  }

  showRemoveAccessConfirmation(payload: Record<string, unknown>): void {
    this.dispatch(
      updateStepFormData({
        stepName: 'RemoveAccessConfirmation',
        formData: payload,
      })
    );

    this.removeAccessAttributePayload =
      payload && typeof payload === 'object' && 'payload' in payload
        ? (payload as { payload: Record<string, unknown> }).payload
        : payload;

    const state = this.getState() as RootState;
    const currentIndex = state.copilot.removeAccessEntitlementIndex;

    const generalFormData = state.copilot.stepFormData.General;
    const systemNameField = generalFormData['securitysystems.systemname'] as
      | { value?: string }
      | string
      | undefined;
    const applicationName = String(
      (systemNameField as { value?: string }).value ?? systemNameField ?? ''
    );

    this.dispatch(
      addMessageToEntitlement({
        entitlementIndex: currentIndex,
        entitlementType: 'removeAccess',
        message: {
          id: `removeAccess-confirmation-${currentIndex}-${Date.now()}`,
          type: 'ai_response',
          subType: 'threeButtonsHelperText',
          timestamp: Date.now(),
          values: [applicationName],
          buttons: [
            { label: 'Specify Prompt', action: 'removeaccess_prompt_yes' },
            {
              label: 'Upload Browser Recording',
              action: 'removeaccess_upload_yes',
            },
            { label: 'Skip for Now', action: 'handle_No_remove_access' },
          ],
        },
      })
    );
  }

  handleRemoveAccessPrompt(userPrompt: string): void {
    const state = this.getState() as RootState;
    const currentIndex = state.copilot.removeAccessEntitlementIndex;

    this.dispatch(
      addMessageToEntitlement({
        entitlementIndex: currentIndex,
        entitlementType: 'removeAccess',
        message: {
          id: Date.now().toString(),
          type: 'user_response',
          subType: 'userResponseYes',
          text: userPrompt,
          timestamp: Date.now(),
        },
      })
    );

    this.dispatch({ type: 'copilot/setPromptFooterEnabled', payload: false });
    this.processRemoveAccessPrompt(userPrompt, currentIndex).catch(() => {});
  }

  handleRemoveAccessPromptYes(): void {
    // Get current entitlement index from Redux state instead of stale payload
    const state = this.getState() as RootState;
    const currentIndex = state.copilot.removeAccessEntitlementIndex ?? 0;

    this.dispatch(
      addMessageToEntitlement({
        entitlementIndex: currentIndex,
        entitlementType: 'removeAccess',
        message: {
          id: `user-prompt-yes-${currentIndex}-${Date.now()}`,
          type: 'user_response',
          subType: 'userResponseYes',
          text: 'Specify Prompt',
          timestamp: Date.now(),
        },
      })
    );

    this.dispatch({ type: 'copilot/setPromptFooterEnabled', payload: true });
    this.dispatch({ type: 'copilot/setPromptFooterMode', payload: 'text' });
  }

  handleRemoveAccessUploadYes(): void {
    // Get current entitlement index from Redux state instead of stale payload
    const state = this.getState() as RootState;
    const currentIndex = state.copilot.removeAccessEntitlementIndex;

    this.dispatch(
      addMessageToEntitlement({
        entitlementIndex: currentIndex,
        entitlementType: 'removeAccess',
        message: {
          id: `user-upload-yes-${currentIndex}-${Date.now()}`,
          type: 'user_response',
          subType: 'userResponseYes',
          text: 'Upload Browser Recording',
          timestamp: Date.now(),
        },
      })
    );

    this.dispatch(
      addMessageToEntitlement({
        entitlementIndex: currentIndex,
        entitlementType: 'removeAccess',
        message: {
          id: `upload-file-request-${currentIndex}-${Date.now()}`,
          type: 'ai_response',
          subType: 'provideAdditionalInfo',
          timestamp: Date.now(),
        },
      })
    );

    this.dispatch({ type: 'copilot/setPromptFooterEnabled', payload: true });
    this.dispatch({ type: 'copilot/setPromptFooterMode', payload: 'upload' });
  }

  async handleRemoveAccessNo(): Promise<void> {
    const state = this.getState() as RootState;
    const currentIndex = state.copilot.removeAccessEntitlementIndex;

    this.dispatch(
      addMessageToEntitlement({
        entitlementIndex: currentIndex,
        entitlementType: 'removeAccess',
        message: {
          id: `user-skip-${currentIndex}-${Date.now()}`,
          type: 'user_response',
          subType: 'userResponseNo',
          text: 'Skip for Now',
          timestamp: Date.now(),
        },
      })
    );

    const totalEntitlements = state.copilot.entitlementIteration.entitlements;
    const isLastTemplate = currentIndex >= totalEntitlements.length - 1;

    if (this.removeAccessAttributePayload) {
      try {
        const allPrompts = state.copilot.removeAccessPrompts;
        const { entitlements } = state.copilot.entitlementIteration;

        let payloadToSave = this.removeAccessAttributePayload;

        if ('payload' in this.removeAccessAttributePayload) {
          payloadToSave = (
            this.removeAccessAttributePayload as {
              payload: Record<string, unknown>;
            }
          ).payload;
        }

        const removeAccessTemplate = state.copilot.parsedTemplates
          .RemoveAccess as
          | { attributeDefaultValue?: string | unknown }
          | undefined;
        const originalTemplate = removeAccessTemplate?.attributeDefaultValue;

        if (originalTemplate && typeof originalTemplate === 'string') {
          payloadToSave = buildRemoveAccessCumulativePayload(
            originalTemplate,
            entitlements,
            state.copilot.stepFormData || {},
            currentIndex,
            allPrompts
          );
        }

        let { endpointKey } = state.copilot;

        if (!endpointKey) {
          const urlParams = new URLSearchParams(window.location.search);

          endpointKey =
            parseInt(urlParams.get('endpointKey') ?? '0', 10) || null;
        }

        await this.services.saveUseCaseConfiguration({
          applicationtype: 'CUA',
          endpointKey,
          body: payloadToSave,
        });

        const isEditingExistingSequence =
          isEditModeFromUrl() &&
          currentIndex < totalEntitlements.length - 1 &&
          this.messagesContainEntitlementForm(currentIndex + 1);

        if (isEditingExistingSequence) {
          return;
        }
      } catch (error) {
        console.error('RemoveAccess API call failed:', error);
      }
    }

    if (isLastTemplate) {
      this.dispatch(addCompletedStep('RemoveAccess'));
      this.dispatch(removeSkippedStep('RemoveAccess'));

      const successMessageExists = state.copilot.messages.some(
        (msg) => msg.subType === 'removeAccessUpdateSuccess'
      );

      if (isEditMode(this.getState) && !successMessageExists) {
        this.dispatch(
          addMessage({
            id: `remove-access-flow-success-${Date.now()}`,
            type: 'ai_response',
            subType: 'removeAccessUpdateSuccess',
            text: 'You have completed all Remove Access entitlements. I will add this to the configuration.',
            timestamp: Date.now(),
            addAccessEntitlementIndex: -1,
            removeAccessEntitlementIndex: -1,
            parentEntitlementIndex: undefined,
            entitlementType: undefined,
          })
        );
      }

      const finalConfirmationExists = state.copilot.messages.some(
        (msg) =>
          msg.subType === 'finalConfirmation' ||
          msg.subType === 'finalConfirmationRedirection'
      );

      if (!isEditMode(this.getState) && !finalConfirmationExists) {
        this.showFinalConfirmation?.();
      }

      return;
    }

    this.showNextEntitlementForm(currentIndex + 1);
  }

  handleRemoveAccessFile(file: File): void {
    const state = this.getState() as RootState;
    const currentIndex = state.copilot.removeAccessEntitlementIndex;

    this.dispatch(
      addMessageToEntitlement({
        entitlementIndex: currentIndex,
        entitlementType: 'removeAccess',
        message: {
          id: `analyzing-file-${currentIndex}-${Date.now()}`,
          type: 'ai_response',
          subType: 'analyzing',
          text: `Analyzing ${file.name}...`,
          timestamp: Date.now(),
          loader: true,
        },
      })
    );

    this.dispatch({ type: 'copilot/setPromptFooterEnabled', payload: false });

    setTimeout(() => {
      this.dispatch(removeMessagesBySubType('analyzing'));
      const extractedInstructions = `Instructions extracted from ${file.name}`;

      this.handleRemoveAccessPromptWithIndex(extractedInstructions).catch(
        () => undefined
      );
    }, 2000);
  }
}
